import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { compact } from 'lodash-es';
import { StatusBadge, DropdownMenuItem, TableList, TableListActions, Typography } from 'seagull';
import { Archive, Edit, Eye, Trash, Unarchive } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useMultiVendorClientTooltip } from '~/hooks/useMultiVendorClientTooltip';
import { useLocaleKeys } from '~/i18n';
import { eligibilityTooltipTitle, eligibilityTooltipsTextsPath } from '../ClientsPage.utils';
import { ContactsCell } from './ContactsCell';
const CLIENT_STATUS_BADGE_SKIN = {
    active: 'success',
    archived: 'default'
};
export const GetClientRowTID = (parentTestId)=>getTestIds(parentTestId);
export function ClientRow(param) {
    let { client, testId, onEditClient, onViewClient, onArchiveClient, onRestoreClient, onDeleteClient } = param;
    var _client_type;
    const { t: clientsDashboardTexts } = useLocaleKeys('client.dashboard');
    const { t: clientStatusTexts } = useLocaleKeys('client.statusLabels');
    const { t: clientTypeTexts } = useLocaleKeys('client.typeLabels');
    const { t: eligibilityTooltipsTexts } = useLocaleKeys(eligibilityTooltipsTextsPath);
    const { ClientRowTestIds: TID } = useMemo(()=>GetClientRowTID(testId), [
        testId
    ]);
    const clientTypeText = clientTypeTexts[(_client_type = client.type) !== null && _client_type !== void 0 ? _client_type : 'business']();
    const isActive = client.status === 'active';
    const isArchived = client.status === 'archived';
    const canArchive = client.archiveEligibility.type !== 'notEligible';
    const canDelete = client.deleteEligibility.type !== 'notEligible';
    const eligibilityArchiveTooltipTitle = eligibilityTooltipTitle(client.archiveEligibility, 'archive', eligibilityTooltipsTexts);
    const deleteTooltipTitle = eligibilityTooltipTitle(client.deleteEligibility, 'delete', eligibilityTooltipsTexts);
    const isMultiVendorClient = client.isMultiVendorCustomer;
    const multiVendorTooltipTitle = useMultiVendorClientTooltip(isMultiVendorClient);
    const archiveTooltipTitle = multiVendorTooltipTitle !== null && multiVendorTooltipTitle !== void 0 ? multiVendorTooltipTitle : eligibilityArchiveTooltipTitle;
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: client.id,
        testId: TID.Root(),
        onClick: ()=>onViewClient(client),
        items: [
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    variant: "paragraph1",
                    testId: TID.Name(),
                    children: client.name
                })
            },
            {
                value: client.status ? /*#__PURE__*/ _jsx(StatusBadge, {
                    skin: CLIENT_STATUS_BADGE_SKIN[client.status],
                    testId: TID.Status(),
                    children: clientStatusTexts[client.status]()
                }) : null
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    weight: "light",
                    testId: TID.Type(),
                    children: clientTypeText
                })
            },
            {
                value: /*#__PURE__*/ _jsx(ContactsCell, {
                    client: client,
                    testId: TID.Contacts()
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsx(TableListActions, {
            bi: {
                button: {
                    name: 'clients_view'
                },
                menuTrigger: {
                    name: 'clients_actions'
                }
            },
            testId: TID.Actions(),
            btnHeapId: "client-view-btn",
            buttonText: clientsDashboardTexts.actions.view(),
            buttonIcon: /*#__PURE__*/ _jsx(Eye, {
                testId: TID.ActionViewIcon()
            }),
            onClickButton: ()=>onViewClient(client),
            mobile: {
                title: client.name,
                subtitle: clientTypeText
            },
            children: compact([
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'client_edit'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                    onClick: ()=>onEditClient(client),
                    testId: TID.ActionEdit(),
                    disabled: isMultiVendorClient,
                    tooltipProps: multiVendorTooltipTitle ? {
                        title: multiVendorTooltipTitle,
                        placement: 'bottom'
                    } : undefined,
                    children: clientsDashboardTexts.actions.edit()
                }, "edit"),
                isActive && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'client_archive'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Archive, {}),
                    onClick: ()=>onArchiveClient(client),
                    testId: TID.ActionArchive(),
                    disabled: !canArchive || client.isSample || isMultiVendorClient,
                    tooltipProps: archiveTooltipTitle ? {
                        title: archiveTooltipTitle,
                        placement: 'bottom'
                    } : undefined,
                    children: clientsDashboardTexts.actions.archive()
                }, "archive"),
                isArchived && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'client_restore'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Unarchive, {}),
                    onClick: ()=>onRestoreClient(client),
                    testId: TID.ActionRestore(),
                    disabled: isMultiVendorClient,
                    tooltipProps: multiVendorTooltipTitle ? {
                        title: multiVendorTooltipTitle,
                        placement: 'bottom'
                    } : undefined,
                    children: clientsDashboardTexts.actions.unarchive()
                }, "restore"),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'client_delete'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    onClick: ()=>onDeleteClient(client),
                    testId: TID.ActionDelete(),
                    disabled: !canDelete || client.isSample,
                    skin: "error",
                    tooltipProps: deleteTooltipTitle ? {
                        title: deleteTooltipTitle,
                        placement: 'bottom'
                    } : undefined,
                    children: clientsDashboardTexts.actions.delete()
                }, "delete")
            ])
        })
    });
}
